import { DASHBOARD_PERMISSIONS } from "./dashboard";
import type { Permission } from "./permissions";

/**
 * Every page an operator can open from the chrome, in the rail's order. Shared by
 * the nav rail and the spotlight search, so a page added here is reachable from
 * both and neither can drift from the other.
 */

export interface Destination {
  readonly to: string;
  readonly label: string;
  /** Grouping caption printed above the first item of each group. */
  readonly group: string;
  /**
   * Draws the open-alert count beside the label. One destination has it, and it is
   * the only number in the rail: a badge on every item would be a dashboard, and
   * the point of this one is that it is unusual for it to be there at all.
   */
  readonly badge?: "alerts";
  /**
   * The `.view` permission the page's own list needs. With roles, nothing is held
   * by everyone any more — a support role may see orders and customers and no
   * stations — so every destination names one, and the rail shows only what the
   * principal can open.
   *
   * The page still renders its own explanation on a 403; this only keeps the rail
   * from advertising a destination the account cannot use.
   *
   * A list means any one of them. The dashboard is the case: each of its panels
   * has its own permission, and holding one is a page worth opening.
   */
  readonly permission: Permission | readonly Permission[];
}

/** Whether the principal may open the destination. Shared by the rail and the spotlight. */
export function canOpen(
  destination: Destination,
  can: (permission: Permission) => boolean,
): boolean {
  const { permission } = destination;
  return typeof permission === "string" ? can(permission) : permission.some((one) => can(one));
}

export const DESTINATIONS: readonly Destination[] = [
  /**
   * First of all: the page `/` lands on, and the one an operator starts a shift
   * from. Its panels are each gated on their own data's permission, so the entry
   * shows for anybody holding one of them.
   */
  { to: "/dashboard", label: "Overview", group: "Operations", permission: DASHBOARD_PERMISSIONS },
  { to: "/fleet", label: "Charge points", group: "Operations", permission: "charge_points.view" },
  { to: "/depots", label: "Depots", group: "Operations", permission: "depots.view" },
  { to: "/sessions", label: "Sessions", group: "Operations", permission: "sessions.view" },
  /**
   * Under Operations rather than a group of its own: an operator's day is the
   * fleet, and public charging is one more thing happening on it. Its refund queue
   * is money owed today, which is not an audit concern.
   */
  {
    to: "/public-orders",
    label: "Public charging",
    group: "Operations",
    permission: "public_orders.view",
  },
  /**
   * After public charging, because that is where an operator arrives from: a
   * driver's question is almost always about a charge, and the account is how
   * they are found. On its own it is a directory, which nobody opens for its own
   * sake (§20.7).
   */
  { to: "/customers", label: "Customers", group: "Operations", permission: "customers.view" },
  /**
   * Beside public charging rather than under a group of its own: a price only
   * matters because a driver is quoted it, and an operator arrives here from
   * "this station has nothing to sell at" rather than looking for a price list.
   */
  { to: "/prices", label: "Prices", group: "Operations", permission: "tariffs.view" },
  /**
   * First in Allowances: a policy is assigned to a holder and pooled over its
   * subtree, so this is where an allowance is set, and a card's page links here.
   */
  { to: "/holders", label: "Holders", group: "Allowances", permission: "identities.view" },
  { to: "/identities", label: "Identities", group: "Allowances", permission: "identities.view" },
  { to: "/policies", label: "Policies", group: "Allowances", permission: "policies.view" },
  /**
   * First in Operations, ahead of the fleet itself, because it is the one
   * destination that is only worth opening when something is wrong — and when
   * something is wrong it is what an operator should open first. The badge is what
   * makes that ordering honest: with nothing open the item is quiet and costs a
   * line of nav, and with a condition open it is the only number on the screen.
   */
  {
    to: "/alerts",
    label: "Alerts",
    group: "Operations",
    badge: "alerts",
    permission: "alerts.view",
  },
  /**
   * Beside alerts, and for the same kind of moment: a station that will not come
   * online. What it tried at the handshake is the one thing the fleet list
   * cannot show, because a station that never got in has no frames.
   */
  { to: "/connections", label: "Connections", group: "Operations", permission: "connections.view" },
  { to: "/events", label: "Events", group: "Audit", permission: "events.view" },
  /**
   * Beside the event feed, which is the other thing in this group somebody opens
   * to reconstruct what happened — and one group away from Operations, because
   * reading the trail is not part of a shift.
   */
  { to: "/audit", label: "Audit log", group: "Audit", permission: "audit.view" },
  /**
   * After the audit trail: the other place somebody goes to reconstruct what
   * happened, one level down — what the server did, rather than what a person did.
   */
  { to: "/logs", label: "Server logs", group: "Audit", permission: "logs.view" },
  /**
   * Its own group, last, and deliberately far from Operations: what is here is not
   * a day's work, it is the standing decision the day's work happens under — how a
   * public charge takes a driver's money. A setting that changes what a stranger's
   * card is debited should not sit one item away from the station list somebody
   * clicks forty times a shift.
   */
  { to: "/settings", label: "Settings", group: "Configuration", permission: "settings.view" },
  /**
   * Beside settings: who may sign in here, and what each of them may do, is a
   * standing decision about the deployment rather than a day's work.
   */
  { to: "/operators", label: "Operators", group: "Configuration", permission: "operators.view" },
  /**
   * After operators, because a role is only ever met through the people holding
   * it: an admin arrives here from "why can this person not refund?".
   */
  { to: "/roles", label: "Roles", group: "Configuration", permission: "roles.view" },
  /**
   * Where drivers' money goes: the merchant accounts, and which depots each one
   * serves. Set up once per merchant, then rarely touched — beside Apps, the other
   * page that holds credentials nobody may read back.
   */
  {
    to: "/payment-providers",
    label: "Payment providers",
    group: "Configuration",
    permission: "payment_providers.view",
  },
  /**
   * Last: who outside the company may read data through the API, and how often.
   * Set up once per partner and then left alone.
   */
  { to: "/apps", label: "Apps", group: "Configuration", permission: "apps.view" },
  /**
   * Right under Apps: what the Apps that push sent, and what came back. The App
   * page says where sessions go; this is the wire, for "we never got it".
   */
  {
    to: "/session-pushes",
    label: "Session pushes",
    group: "Configuration",
    permission: "apps.view",
  },
];
